import { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Box,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TablePagination,
    useTheme,
    useMediaQuery,
    Stack,
    Card,
    Collapse,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { usePermission } from "../hooks/usePermission";
import config from "../config/config";
import { useToast } from "../context/ToastContext";
import ConfirmDialog from "../components/custom-dialog/confirm-dialog";
import { useBoolean } from "../hooks/use-boolean";
import { formatNumber, parseNumber } from "../utils/helpers";
import InvoiceModal from "../components/InvoiceModal";
import CustomerDetailDialog from "../components/Customers/CustomerDetailDialog";

const CustomerRow = ({ customer, onEdit, onDelete, onShowInfo }) => {
    const [open, setOpen] = useState(false);
    const [orders, setOrders] = useState([]);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedOrderForInvoice, setSelectedOrderForInvoice] =
        useState(null);
    const { token } = useAuth();

    const fetchOrders = async () => {
        try {
            const res = await axios.get(
                `${config.backendUrl}/api/orders?customerId=${customer._id}`,
                {
                    headers: { "x-auth-token": token },
                }
            );
            setOrders(res.data.docs || []);
        } catch (err) {
            console.error(err);
        }
    };

    const handleShowInvoiceModal = async (order) => {
        try {
            const res = await axios.get(
                `${config.backendUrl}/api/customers/${order.customer._id}`,
                {
                    headers: { "x-auth-token": token },
                }
            );
            const customer = res.data;
            if (customer) {
                const updatedOrder = {
                    ...order,
                    debt_old:
                        customer?.debt -
                        (order?.totalAmount - order?.amountPaid),
                    debt: customer?.debt,
                };
                setSelectedOrderForInvoice(updatedOrder);
            } else {
                setSelectedOrderForInvoice(order);
            }
        } catch (error) {
            console.error("Error fetching customer data:", error);
            setSelectedOrderForInvoice(order); // Fallback to original order data
        }

        setShowInvoiceModal(true);
    };

    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setSelectedOrderForInvoice(null);
    };

    return (
        <>
            <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => {
                            setOpen(!open);
                            if (!open) {
                                fetchOrders();
                            }
                        }}
                    >
                        {open ? (
                            <KeyboardArrowUpIcon />
                        ) : (
                            <KeyboardArrowDownIcon />
                        )}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {customer.name}
                </TableCell>
                <TableCell>{customer.phone}</TableCell>
                <TableCell>{`${customer?.address?.street || ""}, ${
                    customer?.address?.city || ""
                }, ${customer?.address?.state || ""}, ${
                    customer?.address?.country || ""
                }`}</TableCell>
                <TableCell>{customer?.debt?.toLocaleString() || 0}</TableCell>
                <TableCell>
                    {customer.debtSince
                        ? Math.floor(
                              (new Date() - new Date(customer.debtSince)) /
                                  (1000 * 60 * 60 * 24)
                          ) + " ngày"
                        : 0}
                </TableCell>
                <TableCell>
                    <IconButton
                        onClick={() => onEdit(customer)}
                        color="primary"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => onShowInfo(customer, true)}
                        color="info"
                    >
                        <InfoIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => onDelete(customer._id)}
                        color="secondary"
                    >
                        <DeleteIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={6}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                component="div"
                            >
                                Lịch sử đơn hàng
                            </Typography>
                            <Table size="small" aria-label="purchases">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Mã Đơn hàng</TableCell>
                                        <TableCell>Sản phẩm(SL)</TableCell>
                                        <TableCell>Tổng số tiền</TableCell>
                                        <TableCell>Đã thanh toán</TableCell>
                                        <TableCell>Ngày tạo</TableCell>
                                        <TableCell>Hành động</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orders.map((order) => (
                                        <TableRow key={order._id}>
                                            <TableCell>
                                                {order.orderCode}
                                            </TableCell>
                                            <TableCell>
                                                {order.items.map((item) => (
                                                    <div key={item._id}>
                                                        {item?.product?.name}{" "}
                                                        (SL: {item?.quantity})
                                                    </div>
                                                ))}
                                            </TableCell>
                                            <TableCell>
                                                {formatNumber(
                                                    order.totalAmount
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatNumber(order.amountPaid)}
                                            </TableCell>
                                            <TableCell>
                                                {new Date(
                                                    order.createdAt
                                                ).toLocaleDateString("vi-VN")}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    onClick={() =>
                                                        handleShowInvoiceModal(
                                                            order
                                                        )
                                                    }
                                                >
                                                    Xem hóa đơn
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            <InvoiceModal
                show={showInvoiceModal}
                handleClose={handleCloseInvoiceModal}
                order={selectedOrderForInvoice}
            />
        </>
    );
};

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [totalDebt, setTotalDebt] = useState(0);
    const [open, setOpen] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [currentCustomer, setCurrentCustomer] = useState(null);
    const confirm = useBoolean();
    const [deleteCustomerId, setDeleteCustomerId] = useState(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        address: {
            street: "",
            city: "",
            state: "",
            country: "",
        },
        debt: 0,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const { token } = useAuth();
    const { hasPermission } = usePermission();
    const { showToast } = useToast();

    const [pageMobile, setPageMobile] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [orders, setOrders] = useState([]);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedOrderForInvoice, setSelectedOrderForInvoice] =
        useState(null);
    const [ordersPage, setOrdersPage] = useState(1);
    const [hasMoreOrders, setHasMoreOrders] = useState(true);

    useEffect(() => {
        if (!isMobile) {
            fetchCustomers();
            fetchDashboardStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, page, rowsPerPage, isMobile]);

    // Với mobile: reset danh sách khi tìm kiếm
    useEffect(() => {
        if (isMobile) {
            setCustomers([]);
            setPageMobile(0);
            fetchCustomers(false, 1); // Fetch page 1
            fetchDashboardStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, isMobile]);

    const fetchDashboardStats = async () => {
        try {
            const res = await axios.get(
                `${config.backendUrl}/api/dashboard/stats`,
                {
                    headers: { "x-auth-token": token },
                }
            );
            setTotalDebt(res.data.totalDebt || 0);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchCustomers = async (append = false, pageOverride = null) => {
        try {
            const params = {
                page:
                    pageOverride !== null
                        ? pageOverride
                        : isMobile
                        ? pageMobile + 1
                        : page + 1,
                limit: rowsPerPage,
            };
            if (searchQuery) params.search = searchQuery;

            const res = await axios.get(`${config.backendUrl}/api/customers`, {
                headers: { "x-auth-token": token },
                params,
            });

            const newCustomers = res.data.docs || [];
            const total = res.data.totalDocs || 0;

            if (append) {
                setCustomers((prev) => [...prev, ...newCustomers]);
            } else {
                setCustomers(newCustomers);
            }

            setTotalCustomers(total);
            const currentPage =
                pageOverride !== null
                    ? pageOverride
                    : isMobile
                    ? pageMobile + 1
                    : page + 1;
            setHasMore(currentPage * rowsPerPage < total);
            setIsLoadingMore(false);
        } catch (err) {
            console.error(err);
            if (!append) {
                setCustomers([]);
                setTotalCustomers(0);
            }
            setIsLoadingMore(false);
            setHasMore(false);
        }
    };

    const fetchOrders = async (customerId, newPage = 1) => {
        try {
            const res = await axios.get(
                `${config.backendUrl}/api/orders?customerId=${customerId}&page=${newPage}`,
                {
                    headers: { "x-auth-token": token },
                }
            );
            if (newPage === 1) {
                setOrders(res.data.docs || []);
            } else {
                setOrders((prev) => [...prev, ...(res.data.docs || [])]);
            }
            setHasMoreOrders(res.data.hasNextPage);
            setOrdersPage(newPage);
        } catch (err) {
            console.error(err);
        }
    };

    const handleClickOpen = (customer = null, viewMode = false) => {
        setCurrentCustomer(customer);
        setIsViewMode(viewMode);
        if (customer) {
            setForm({
                name: customer.name,
                email: customer.email,
                phone: customer.phone || "",
                address: customer.address || {
                    street: "",
                    city: "",
                    state: "",
                    country: "",
                },
                debt: customer.debt || 0,
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentCustomer(null);
        setIsViewMode(false);
        setForm({
            name: "",
            email: "",
            phone: "",
            debt: 0,
            address: { street: "", city: "", state: "", country: "" },
        });
    };

    const handleOpenDetailDialog = (customer) => {
        setCurrentCustomer(customer);
        fetchOrders(customer._id);
        setOpenDetailDialog(true);
    };

    const handleCloseDetailDialog = () => {
        setOpenDetailDialog(false);
        setCurrentCustomer(null);
        setOrders([]);
        setOrdersPage(1);
        setHasMoreOrders(true);
    };

    const handleShowInvoiceModal = async (order) => {
        try {
            const res = await axios.get(
                `${config.backendUrl}/api/customers/${order.customer._id}`,
                {
                    headers: { "x-auth-token": token },
                }
            );
            const customer = res.data;
            if (customer) {
                const updatedOrder = {
                    ...order,
                    debt_old:
                        customer?.debt -
                        (order?.totalAmount - order?.amountPaid),
                    debt: customer?.debt,
                };
                setSelectedOrderForInvoice(updatedOrder);
            } else {
                setSelectedOrderForInvoice(order);
            }
        } catch (error) {
            console.error("Error fetching customer data:", error);
            setSelectedOrderForInvoice(order); // Fallback to original order data
        }

        setShowInvoiceModal(true);
    };

    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setSelectedOrderForInvoice(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith("address.")) {
            const addressField = name.split(".")[1];
            setForm((prevForm) => ({
                ...prevForm,
                address: {
                    ...prevForm.address,
                    [addressField]: value,
                },
            }));
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {};

        if (!form.name) errors.name = "Tên khách hàng là bắt buộc";
        if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email) && form.email) {
            errors.email = "Email không hợp lệ";
        }
        // if (!form.phone) errors.phone = "Số điện thoại là bắt buộc";

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        try {
            if (currentCustomer) {
                await axios.put(
                    `${config.backendUrl}/api/customers/${currentCustomer._id}`,
                    form,
                    {
                        headers: { "x-auth-token": token },
                    }
                );
                showToast("Khách hàng đã được cập nhật thành công!", "success");
            } else {
                await axios.post(`${config.backendUrl}/api/customers`, form, {
                    headers: { "x-auth-token": token },
                });
                showToast("Khách hàng đã được thêm thành công!", "success");
            }
            fetchCustomers();
            fetchDashboardStats();
            handleClose();
            setValidationErrors({}); // Clear errors on success
        } catch (err) {
            console.error(err);
            let errorMessage = "Đã xảy ra lỗi khi lưu khách hàng.";
            if (err.response && err.response.data) {
                if (err.response.data.errors) {
                    const apiErrors = {};
                    err.response.data.errors.forEach((error) => {
                        apiErrors[error.param] = error.msg;
                    });
                    setValidationErrors(apiErrors);
                    errorMessage =
                        err.response.data.errors[0].msg || errorMessage;
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            }
            showToast(errorMessage, "error");
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(
                `${config.backendUrl}/api/customers/${deleteCustomerId}`,
                {
                    headers: { "x-auth-token": token },
                }
            );
            fetchCustomers();
            fetchDashboardStats();
            showToast("Khách hàng đã được xóa thành công!", "success");
        } catch (err) {
            console.error(err);
            let errorMessage = "Đã xảy ra lỗi khi xóa khách hàng.";
            if (
                err.response &&
                err.response.data &&
                err.response.data.message
            ) {
                errorMessage = err.response.data.message;
            }
            showToast(errorMessage, "error");
        }
        confirm.onFalse();
        setDeleteCustomerId(null);
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ px: { xs: -20, sm: 3, md: 4 }, py: 3 }}>
                <Typography
                    variant={isMobile ? "h6" : "h4"}
                    gutterBottom
                    sx={{ fontWeight: 700, mb: 4, mt: isMobile ? -4 : 4 }}
                >
                    Quản lý Khách hàng
                </Typography>
                <Typography
                    variant={isMobile ? "h7" : "h5"}
                    component="h4"
                    gutterBottom
                    sx={{ textAlign: "left" }}
                >
                    Tổng công nợ:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>
                        {totalDebt.toLocaleString()}
                    </span>{" "}
                    ₫
                </Typography>
                {totalCustomers} Khách hàng
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 2,
                        mb: 2,
                        position: isMobile ? "sticky" : "static",
                        top: isMobile ? "56px" : "auto", // để tránh bị che bởi AppBar
                        zIndex: 10,
                        backgroundColor: "background.paper",
                        p: 0,
                        borderBottom: isMobile ? "1px solid #ddd" : "none",
                    }}
                >
                    <TextField
                        label="Tìm kiếm theo tên, email hoặc điện thoại"
                        variant="outlined"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ flexGrow: 1 }}
                    />
                </Box>
                {hasPermission(["customer:create"]) && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleClickOpen()}
                    >
                        Thêm khách hàng
                    </Button>
                )}
                {isMobile ? (
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        {customers.length > 0 ? (
                            customers.map((customer) => (
                                <Card
                                    key={customer?._id}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        boxShadow: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        position: "relative", // cần thiết để định vị icon Delete
                                    }}
                                >
                                    <Typography fontWeight={600}>
                                        {customer.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        📞 {customer.phone}
                                    </Typography>
                                    <Typography variant="body2">
                                        🏠{" "}
                                        {`${customer?.address?.street || ''}, ${customer?.address?.city  || ''}, ${customer?.address?.state || ''}, ${customer?.address?.country  || ''}`}
                                    </Typography>

                                    {typeof customer?.debt === "number" &&
                                        customer.debt !== 0 && (
                                            <Typography variant="body2">
                                                <CurrencyExchangeIcon
                                                    sx={{
                                                        mr: 1,
                                                        width: 16,
                                                        height: 16,
                                                    }}
                                                />
                                                Công nợ:{" "}
                                                <span
                                                    style={{
                                                        color: "red",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {customer.debt.toLocaleString()}
                                                </span>{" "}
                                                ₫
                                            </Typography>
                                        )}

                                    <Typography variant="body2">
                                        Số ngày nợ:{" "}
                                        {customer.debtSince
                                            ? Math.floor(
                                                  (new Date() -
                                                      new Date(
                                                          customer.debtSince
                                                      )) /
                                                      (1000 * 60 * 60 * 24)
                                              ) + " ngày"
                                            : 0}
                                    </Typography>

                                    <Box>
                                        {hasPermission(["customer:update"]) && (
                                            <IconButton
                                                onClick={() =>
                                                    handleClickOpen(customer)
                                                }
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            onClick={() =>
                                                handleOpenDetailDialog(customer)
                                            }
                                            color="info"
                                        >
                                            <InfoIcon />
                                        </IconButton>
                                    </Box>

                                    {hasPermission(["customer:delete"]) && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                bottom: 16,
                                                right: 8,
                                            }}
                                        >
                                            <IconButton
                                                onClick={() => {
                                                    confirm.onTrue();
                                                    setDeleteCustomerId(
                                                        customer._id
                                                    );
                                                }}
                                                sx={{ color: "red" }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Card>
                            ))
                        ) : (
                            <Typography align="center" color="text.secondary">
                                Không có khách hàng nào.
                            </Typography>
                        )}
                        {isMobile && hasMore && (
                            <Box sx={{ mt: 2, textAlign: "center" }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        const nextPage = pageMobile + 1;
                                        setIsLoadingMore(true);
                                        setPageMobile(nextPage);
                                        fetchCustomers(true, nextPage + 1); // append = true, pageOverride = nextPage + 1
                                    }}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore
                                        ? "Đang tải..."
                                        : "Tải thêm khách hàng"}
                                </Button>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ mt: 4 }}>
                            <Table aria-label="collapsible table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Tên</TableCell>
                                        <TableCell>Điện thoại</TableCell>
                                        <TableCell>Địa chỉ</TableCell>
                                        <TableCell>Số công nợ</TableCell>
                                        <TableCell>Số ngày nợ</TableCell>
                                        <TableCell>Hành động</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {customers.map((customer) => (
                                        <CustomerRow
                                            key={customer._id}
                                            customer={customer}
                                            onEdit={handleClickOpen}
                                            onDelete={() => {
                                                confirm.onTrue();
                                                setDeleteCustomerId(
                                                    customer._id
                                                );
                                            }}
                                            onShowInfo={handleClickOpen}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCustomers}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(
                                    parseInt(event.target.value, 10)
                                );
                                setPage(0);
                            }}
                        />
                    </>
                )}
                <Dialog
                    open={open}
                    fullScreen={isMobile}
                    onClose={(event, reason) => {
                        if (reason !== "backdropClick") {
                            handleClose();
                        }
                    }}
                >
                    <DialogTitle>
                        {isViewMode
                            ? "Chi tiết Khách hàng"
                            : currentCustomer
                            ? "Chỉnh sửa Khách hàng"
                            : "Thêm Khách hàng"}
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{ mt: 1 }}
                        >
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Tên Khách hàng"
                                name="name"
                                value={form?.name}
                                onChange={handleChange}
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                required
                                label="Điện thoại"
                                name="phone"
                                value={form?.phone}
                                onChange={handleChange}
                                error={!!validationErrors.phone}
                                helperText={validationErrors.phone}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Số công nợ"
                                name="debt"
                                value={
                                    form.debt === 0
                                        ? ""
                                        : formatNumber(form.debt)
                                }
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setForm({
                                        ...form,
                                        debt:
                                            val === "" ? "" : parseNumber(val),
                                    });
                                }}
                                // onChange={handleChange}
                                error={!!validationErrors.debt}
                                helperText={validationErrors.debt}
                                InputProps={{
                                    readOnly: isViewMode,
                                    shrink: true,
                                }}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Email"
                                name="email"
                                value={form?.email}
                                onChange={handleChange}
                                error={!!validationErrors.email}
                                helperText={validationErrors.email}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Đường"
                                name="address.street"
                                value={form?.address?.street}
                                onChange={handleChange}
                                error={!!validationErrors["address.street"]}
                                helperText={validationErrors["address.street"]}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Thành phố"
                                name="address.city"
                                value={form?.address?.city}
                                onChange={handleChange}
                                error={!!validationErrors["address.city"]}
                                helperText={validationErrors["address.city"]}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Tỉnh"
                                name="address.state"
                                value={form?.address?.state}
                                onChange={handleChange}
                                error={!!validationErrors["address.state"]}
                                helperText={validationErrors["address.state"]}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Đóng</Button>
                        {!isViewMode && (
                            <Button onClick={handleSubmit}>
                                {currentCustomer ? "Cập nhật" : "Thêm"}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
                <CustomerDetailDialog
                    open={openDetailDialog}
                    onClose={handleCloseDetailDialog}
                    customer={currentCustomer}
                    orders={orders}
                    onViewInvoice={handleShowInvoiceModal}
                    onLoadMore={() =>
                        fetchOrders(currentCustomer._id, ordersPage + 1)
                    }
                    hasMore={hasMoreOrders}
                />
                <InvoiceModal
                    show={showInvoiceModal}
                    handleClose={handleCloseInvoiceModal}
                    order={selectedOrderForInvoice}
                />
                <ConfirmDialog
                    open={confirm.value}
                    onClose={confirm.onFalse}
                    title="Xóa"
                    content="Bạn có chắc chắn muốn xóa?"
                    action={
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDelete}
                        >
                            Xóa
                        </Button>
                    }
                />
            </Box>
        </Container>
    );
};

export default Customers;
